/**
 * The noncentral t distribution, for power analysis.
 *
 * A t statistic computed when the null hypothesis is false follows a noncentral t with
 * noncentrality δ (the standardised true effect times √n, give or take a constant that
 * depends on the design). Power is the probability that this statistic lands beyond the
 * critical value, so every exact t-test power figure — G*Power's, R's `power.t.test`,
 * `pwr` — is a noncentral t tail.
 *
 * The algorithm is AS 243 (Lenth 1989, Applied Statistics 38(1), 185–189): Guenther's
 * twin series of Poisson-weighted incomplete beta functions, with the refinements R's
 * `pnt()` added — a 1e-12 error bound, a 1000-term limit, an accurate start when δ² is
 * small, and the Abramowitz & Stegun 26.7.10 normal approximation when df is huge or δ
 * so large that the Poisson weights underflow. `stats-distributions.test.ts` pins it
 * against `scipy.stats.nct`.
 */

import { normalCdf, studentTCdf } from "@/lib/stats/distributions";
import { logGamma, regularizedBeta } from "@/lib/stats/special";

const ITERATION_LIMIT = 1000;
const ERROR_BOUND = 1e-12;
/** δ² beyond this underflows exp(−δ²/2); R switches to the normal approximation here. */
const UNDERFLOW_LAMBDA = 2 * Math.LN2 * 1021;
const LOG_SQRT_PI = 0.5 * Math.log(Math.PI);
const SQRT_2_OVER_PI = Math.sqrt(2 / Math.PI);

/** Abramowitz & Stegun 26.7.10, for very large df or very large δ. */
function normalApproximation(t: number, df: number, ncp: number): number {
  const s = 1 / (4 * df);
  return normalCdf(t * (1 - s), ncp, Math.sqrt(1 + t * t * 2 * s));
}

/**
 * Guenther's twin series for P(0 < T ≤ t) − Φ(−δ) with t > 0, before the final normal
 * term is added.
 */
function twinSeries(t: number, df: number, del: number): number {
  const x = (t * t) / (t * t + df);
  if (x <= 0) {
    return 0;
  }
  const lambda = del * del;
  let p = 0.5 * Math.exp(-0.5 * lambda);
  let q = SQRT_2_OVER_PI * p * del;
  let s = 0.5 - p;
  if (s < 1e-7) {
    s = -0.5 * Math.expm1(-0.5 * lambda);
  }
  let a = 0.5;
  const b = 0.5 * df;
  const rxb = (df / (t * t + df)) ** b;
  const logBetaHalfB = LOG_SQRT_PI + logGamma(b) - logGamma(0.5 + b);
  let xodd = regularizedBeta(x, a, b);
  let godd = 2 * rxb * Math.exp(a * Math.log(x) - logBetaHalfB);
  const bx = b * x;
  let xeven = bx < Number.EPSILON ? bx : 1 - rxb;
  let geven = bx * rxb;
  let total = p * xodd + q * xeven;
  for (let it = 1; it <= ITERATION_LIMIT; it++) {
    a += 1;
    xodd -= godd;
    xeven -= geven;
    godd *= (x * (a + b - 1)) / a;
    geven *= (x * (a + b - 0.5)) / (a + 0.5);
    p *= lambda / (2 * it);
    q *= lambda / (2 * it + 1);
    total += p * xodd + q * xeven;
    s -= p;
    // Rounding has exhausted the Poisson mass: the series cannot improve further.
    if (s < -1e-10 || (s <= 0 && it > 1)) {
      break;
    }
    if (Math.abs(2 * s * (xodd - godd)) < ERROR_BOUND) {
      break;
    }
  }
  return total;
}

/** P(T ≤ t) for the noncentral t with `df` degrees of freedom and noncentrality `ncp`. */
export function noncentralTCdf(t: number, df: number, ncp: number): number {
  if (ncp === 0) {
    return studentTCdf(t, df);
  }
  if (Number.isNaN(t)) {
    return Number.NaN;
  }
  if (!Number.isFinite(t)) {
    return t < 0 ? 0 : 1;
  }
  const negative = t < 0;
  // P(T ≤ t; δ) = 1 − P(T ≤ −t; −δ), so the series only ever sees t ≥ 0.
  if (negative && ncp > 40) {
    return 0;
  }
  const tt = negative ? -t : t;
  const del = negative ? -ncp : ncp;
  if (df > 4e5 || del * del > UNDERFLOW_LAMBDA) {
    const lower = normalApproximation(tt, df, del);
    return negative ? 1 - lower : lower;
  }
  const tnc = Math.min(1, twinSeries(tt, df, del) + normalCdf(-del));
  return negative ? 1 - tnc : tnc;
}

/** P(T > t) for the noncentral t. */
export function noncentralTUpper(t: number, df: number, ncp: number): number {
  return 1 - noncentralTCdf(t, df, ncp);
}
